// La fiche d'un avantage, côté membre (D-156, maquettes a1 à a3) : « Utiliser » ne change pas ; la fiche montre un QR qui
// change toutes les 30 s et ne sert qu'une fois, un code à 6 chiffres de secours (l'offre d'un partenaire), « En attente de
// la validation… », puis l'écran passe au vert tout seul (« Validé chez …, merci ! », ce qu'il reste). Le QR et le code se
// calculent ici, à partir de la clé reçue à l'ouverture (ils continuent hors ligne) ; la validation est suivie en
// interrogeant la base toutes les 2 s tant que la fiche est ouverte (et dès que le réseau revient). L'appui de 5 s reste en
// lien discret (« montré à l'écran »). Le QR garde un fond blanc en thème sombre.
import './perk-qr.css';
import { openPass, passHold, passState, type PassOpen, type PassState } from '../lib/perk-qr-api';
import { codeSpaced, importQrKey, qrFor, qrUrl, secondsLeft, stepOf } from '../domain/perk-qr';
import { qrEncode, qrPath } from '../domain/qr';
import { FREQ_LABEL, type PerkFrequency } from '../domain/pictos';
import { linksFor } from '../lib/club-links';
import { clubMark } from './theme';
import { partnerMark } from './partners';
import { tv } from './vous';
import { time } from './format';
import { $, html, mount, raw, toast, type Html } from './dom';
import type { Ctx } from './context';

/** Ce que la fiche affiche avant d'ouvrir le passage. */
export interface QrPerkView {
  perkId: string; personId: string | null; title: string; picto: string; details: string | null; frequency: PerkFrequency;
  partner_name: string | null; partner_logo: string | null; stock_left: number | null; for_name: string | null;
}
/** L'appui de 5 s, ouvert depuis la fiche (perks.ts) : `back` ramène au QR. */
export type HoldOpener = (run: () => ReturnType<typeof passHold>, back: () => void, onDone: () => void) => void;

const POLL_MS = 2000;

/** Le QR, tracé par l'encodeur de l'app (D-111) : fond blanc, modules noirs, en clair comme en sombre. */
export function qrSvg(text: string, label = 'QR code à montrer'): Html {
  const c = qrEncode(text), n = c.size + 8;
  return raw(`<svg viewBox="0 0 ${n} ${n}" shape-rendering="crispEdges" role="img" aria-label="${label}"><rect width="${n}" height="${n}" fill="#fff"/><path d="${qrPath(c)}" fill="#111"/></svg>`);
}

export function openPerkQr(ctx: Ctx, p: QrPerkView, hold: HoldOpener, onDone: () => void): void {
  const club = ctx.club;
  const host = document.createElement('div');
  host.className = 'pk-over qx-over';
  host.setAttribute('role', 'dialog');
  host.setAttribute('aria-modal', 'true');
  host.setAttribute('aria-label', `Utiliser : ${p.title}`);
  document.body.append(host);
  mount(host, html`<div class="pk-stage qx-stage"><div class="loading" aria-busy="true"><div class="ball spin" aria-hidden="true"></div></div></div>`);
  let ticker = 0, poller = 0, closed = false, done = false, polling = false;
  const close = (changed = false) => {
    if (closed) return;
    closed = true;
    clearInterval(ticker); clearInterval(poller);
    window.removeEventListener('online', poll);
    document.removeEventListener('keydown', onEsc);
    host.remove();
    if (changed || done) onDone();
  };
  const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !host.hidden) close(); };
  document.addEventListener('keydown', onEsc);
  const t = (tu: string, vous: string) => tv(ctx, tu, vous);
  const partner = !!p.partner_name;

  let pass: PassOpen | null = null;
  async function poll() {
    if (!pass || closed || polling || done) return;
    polling = true;
    try { const s = await passState(pass.id); if (!closed) onState(s); }
    catch { /* hors ligne : on réessaie au prochain tour, ou dès que le réseau revient */ }
    finally { polling = false; }
  }
  function onState(s: PassState) {
    if (s.status === 'used' && s.method !== 'hold') { done = true; showValidated(s); return; }
    if (s.status === 'replaced' || s.status === 'expired' || s.status === 'gone') showStale(s.status);
  }

  void (async () => {
    try {
      pass = await openPass(p.perkId, p.personId);
    } catch (e) {
      const m = String((e as Error).message);
      close();
      toast(m.includes('indisponible') ? 'Cet avantage n’est plus disponible.' : t('Pas de réseau : réessaie dans un instant.', 'Pas de réseau : réessayez dans un instant.'), 'ko');
      if (m.includes('indisponible')) onDone();
      return;
    }
    if (closed) return;
    const offset = pass.now - Date.now();
    const key = await importQrKey(pass.key);
    const origin = (await linksFor(club?.id).catch(() => null))?.origin ?? location.origin;
    const who = `${pass.who} · ${club?.name ?? ''}`;
    const left = pass.stock_left ?? p.stock_left;
    mount(host, html`
      <div class="pk-stage qx-stage">
        <button type="button" class="pk-x" aria-label="Fermer">×</button>
        <div class="qx-who">${partner ? partnerMark({ name: p.partner_name!, logo: p.partner_logo }, 'pt-logo') : clubMark(club, 'sm')}
          <span><b>${p.partner_name ?? club?.name ?? ''}</b><small>${partner ? 'Offre réservée aux membres' : 'Avantage du club'}</small></span></div>
        <h2 class="pk-title qx-title">${p.title}</h2>
        ${p.details ? html`<p class="qx-det">${p.details}</p>` : ''}
        <span class="pk-chips"><span class="chip chip-mute">${FREQ_LABEL[p.frequency]}</span>${left != null ? html`<span class="chip chip-accent">Plus que ${left}</span>` : ''}</span>
        ${pass.for ? html`<p class="qx-for">Pour ${pass.for}</p>` : ''}
        <div class="qx-qr-card" data-guide="qx-qr">
          <span class="qx-qr" id="qx-qr"></span>
          <p class="qx-qr-name">${clubMark(club, 'sm')}<span>${who}</span></p>
          <span class="qx-timer" aria-hidden="true"><i id="qx-bar"></i></span>
          <p class="qx-renew" id="qx-renew" aria-live="off">Nouveau QR dans 30 s · il ne sert qu’une fois</p>
        </div>
        <p class="qx-show">${partner ? t('Montre ce QR au commerçant : il le scanne avec l’appareil photo de son téléphone.', 'Montrez ce QR au commerçant : il le scanne avec l’appareil photo de son téléphone.')
          : t('Montre ce QR au bureau ou au bénévole de service : il le scanne avec l’appareil photo de son téléphone.', 'Montrez ce QR au bureau ou au bénévole de service : il le scanne avec l’appareil photo de son téléphone.')}</p>
        ${partner ? html`<p class="qx-code-l">Le scan ne passe pas ? Il tape ce code</p>
          <p class="qx-code" id="qx-code" data-guide="qx-code" aria-live="polite">••• •••</p>` : ''}
        <p class="qx-wait" id="qx-wait"><span class="qx-dot" aria-hidden="true"></span>En attente de la validation…</p>
        <button type="button" class="qx-alt" id="qx-alt">${partner ? 'Pas de téléphone chez le commerçant ?' : 'Pas de téléphone pour scanner ?'} <b>Appui de 5 s ›</b></button>
      </div>`);
    $(host, '.pk-x')!.addEventListener('click', () => close());
    let step = -1, shown = -1;
    // Le temps qui reste : la barre est une animation CSS relancée à chaque QR (aucune écriture dans la page entre deux),
    // le texte change une fois par seconde.
    const draw = async () => {
      if (closed || done) return;
      const now = Date.now() + offset;
      const s = stepOf(now, pass!.period);
      const sec = secondsLeft(now, pass!.period);
      const renew = $(host, '#qx-renew');
      if (renew && sec !== shown) { shown = sec; renew.textContent = `Nouveau QR dans ${sec} s · il ne sert qu’une fois`; }
      if (s === step) return;
      step = s;
      const bar = $(host, '#qx-bar');
      if (bar) {
        const i = document.createElement('i');
        i.id = 'qx-bar';
        i.style.animationDuration = `${pass!.period}s`;
        i.style.animationDelay = `-${((now / 1000) % pass!.period).toFixed(2)}s`;
        bar.replaceWith(i);
      }
      const q = await qrFor(key, pass!.ref, s);
      if (closed || done || s !== step) return;
      const box = $(host, '#qx-qr');
      if (box) { box.innerHTML = qrSvg(qrUrl(origin, q.token)).s; box.dataset.token = q.token; }
      const code = $(host, '#qx-code');
      if (code) { code.textContent = codeSpaced(q.code); code.setAttribute('aria-label', `Code ${q.code.split('').join(' ')}`); }
    };
    await draw();
    ticker = window.setInterval(() => void draw(), 250);
    poller = window.setInterval(() => void poll(), POLL_MS);
    window.addEventListener('online', poll);
    $(host, '#qx-alt')!.addEventListener('click', () => {
      host.hidden = true;
      hold(() => passHold(pass!.id), () => { host.hidden = false; }, () => { done = true; close(true); });
    });
  })();

  /** Validé par le partenaire, le bureau ou le bénévole : l'écran passe au vert tout seul (maquette a2). */
  function showValidated(s: PassState) {
    clearInterval(ticker); clearInterval(poller);
    host.hidden = false;
    try { navigator.vibrate?.([60, 40, 120]); } catch { /* sans vibreur */ }
    const at = s.used_at ? new Date(s.used_at) : new Date();
    const where = s.partner ? `Validé chez ${s.partner}, merci !` : s.validator ? `Validé par ${s.validator}, merci !` : 'Validé au club, merci !';
    const left = s.stock_left != null ? ` Il en reste ${s.stock_left} pour les membres du club.` : '';
    host.classList.add('done');
    mount(host, html`
      <div class="pk-ok">
        <span class="pk-pulse" aria-hidden="true"></span>
        <svg class="pk-check" viewBox="0 0 52 52" aria-hidden="true"><circle cx="26" cy="26" r="24"/><path d="M14 27l8 8 16-17"/></svg>
        <p class="pk-ok-eyebrow">Avantage validé</p>
        <h2 class="pk-ok-title">${s.title ?? p.title}</h2>
        <p class="pk-ok-who qx-ok-big" role="status">${where}</p>
        <p class="pk-ok-time">${sameDay(at) ? 'Aujourd’hui' : at.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} · ${time(at)}</p>
        <p class="qx-ok-left">${s.partner ? `${s.partner} applique l’avantage.` : t('C’est compté : il te le remet.', 'C’est compté : il vous le remet.')}${left}</p>
        <span class="pk-ok-club">${clubMark(club, 'sm')}<span>${club?.name ?? ''}</span></span>
        <button type="button" class="btn pk-ok-btn" id="qx-done">Terminé</button>
      </div>`);
    $(host, '#qx-done')!.addEventListener('click', () => close(true));
  }

  /** Un autre QR est ouvert (ailleurs), ou celui-ci a expiré : plus rien ne se valide ici. */
  function showStale(status: string) {
    clearInterval(ticker); clearInterval(poller);
    const card = $(host, '.qx-qr-card');
    if (!card) return;
    card.classList.add('qx-stale');
    mount(card, html`<p class="qx-stale-t">${status === 'replaced' ? 'Ce QR ne sert plus' : 'Ce QR a expiré'}</p>
      <p class="qx-stale-s">${status === 'replaced' ? t('Tu as ouvert cet avantage ou un autre ailleurs : c’est ce QR-là qui compte.', 'Vous avez ouvert cet avantage ou un autre ailleurs : c’est ce QR-là qui compte.')
        : t('Ferme la fiche et rouvre l’avantage pour un nouveau QR.', 'Fermez la fiche et rouvrez l’avantage pour un nouveau QR.')}</p>`);
    for (const id of ['#qx-code', '#qx-wait', '#qx-alt']) $(host, id)?.remove();
    $(host, '.qx-code-l')?.remove();
  }
}

const sameDay = (d: Date) => d.toDateString() === new Date().toDateString();
